import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Icon = ({ d, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const ICONS = {
  dashboard: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
  mosques:   ['M3 21h18', 'M5 21V7l7-4 7 4v14', 'M9 21v-6h6v6'],
  quiz:      ['M9 11l3 3L22 4', 'M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11'],
  questions: ['M8 6h13', 'M8 12h13', 'M8 18h13', 'M3 6h.01', 'M3 12h.01', 'M3 18h.01'],
  stats:     ['M18 20V10', 'M12 20V4', 'M6 20v-6'],
  trophy:    ['M8 21h8', 'M12 17v4', 'M7 4H3v3a4 4 0 0 0 4 4h10a4 4 0 0 0 4-4V4h-4', 'M7 4h10'],
  invoices:  ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
  logs:      ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M12 18v-6', 'M9 15l3 3 3-3'],
  settings:  ['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z'],
  profile:   ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
  logout:    ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 17l5-5-5-5', 'M21 12H9'],
  users:     ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
  menu:      ['M3 6h18', 'M3 12h18', 'M3 18h18'],
  close:     ['M18 6L6 18', 'M6 6l12 12'],
};

const NavItem = ({ to, label, icon, end }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
  >
    <Icon d={ICONS[icon]} />
    {label}
  </NavLink>
);

const LockedNavItem = ({ to, label, icon }) => (
  <NavLink
    to={to}
    className={({ isActive }) => `nav-item locked${isActive ? ' active' : ''}`}
    style={{ opacity: 0.55, position: 'relative' }}
  >
    <Icon d={ICONS[icon]} />
    {label}
    <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    </span>
  </NavLink>
);

const PlanBadge = ({ plan, isTrial }) => {
  const colors = {
    free:     { bg: '#F0EFED', color: '#666' },
    standard: { bg: '#E8F0FE', color: '#1A56DB' },
    premium:  { bg: '#FBF5E6', color: '#8B6A1C' },
  };
  const c = colors[plan] || colors.free;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px',
      borderRadius: 100, textTransform: 'uppercase', letterSpacing: 0.5,
      background: c.bg, color: c.color,
    }}>
      {plan || 'Free'}{isTrial ? ' Trial' : ''}
    </span>
  );
};

function useMobileMenu() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return [open, setOpen];
}

export const SuperAdminSidebar = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useMobileMenu();

  return (
    <>
      <div className="mobile-top-bar">
        <button className="mobile-menu-btn" onClick={() => setOpen(o => !o)} aria-label="Menü">
          <Icon d={open ? ICONS.close : ICONS.menu} size={22} />
        </button>
        <span className="mobile-logo">
          Ilmi<span style={{ color: 'var(--accent)' }}>fy</span>
        </span>
        <div style={{ width: 38 }} />
      </div>

      {open && <div className="sidebar-overlay" onClick={() => setOpen(false)} />}

      <aside className={`sidebar${open ? ' sidebar-open' : ''}`}>
        <div className="sidebar-logo">
          <h2>Ilmi<span style={{ color: 'var(--accent)' }}>fy</span></h2>
          <p>Super Administrator</p>
        </div>

        <nav className="sidebar-nav">
          <NavItem to="/superadmin" label="Dashboard" icon="dashboard" end />
          <NavItem to="/superadmin/mosques" label="Moscheen" icon="mosques" />
          <NavItem to="/superadmin/questions" label="Fragen-Datenbank" icon="questions" />
          <NavItem to="/superadmin/invoices" label="Rechnungen" icon="invoices" />
          <NavItem to="/superadmin/logs" label="Aktivitäten" icon="logs" />
          <NavItem to="/superadmin/backups" label="Backups" icon="settings" />
          <NavItem to="/superadmin/uptime" label="Uptime & Status" icon="stats" />
          <NavItem to="/superadmin/settings" label="Einstellungen" icon="settings" />
        </nav>

        <div className="sidebar-footer">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px 12px' }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%',
              background: 'var(--primary-lt)', color: 'var(--primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700, flexShrink: 0,
            }}>
              {user?.firstName?.[0] || 'A'}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.firstName} {user?.lastName}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>Super Admin</div>
            </div>
          </div>
          <button
            className="btn btn-ghost w-full"
            style={{ justifyContent: 'center', gap: 8, fontSize: 13 }}
            onClick={() => { logout(); navigate('/login'); }}
          >
            <Icon d={ICONS.logout} size={15} /> Abmelden
          </button>
        </div>
      </aside>
    </>
  );
};

export const AdminSidebar = () => {
  const { logout, mosque, plan, hasFeature, isViewer } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useMobileMenu();

  return (
    <>
      <div className="mobile-top-bar">
        <button className="mobile-menu-btn" onClick={() => setOpen(o => !o)} aria-label="Menü">
          <Icon d={open ? ICONS.close : ICONS.menu} size={22} />
        </button>
        <span className="mobile-logo" style={{ maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 17 }}>
          {mosque?.name || <>Ilmi<span style={{ color: 'var(--accent)' }}>fy</span></>}
        </span>
        <button
          onClick={() => { logout(); navigate('/login'); }}
          style={{ width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', borderRadius: 8 }}
          aria-label="Abmelden"
        >
          <Icon d={ICONS.logout} size={18} />
        </button>
      </div>

      {open && <div className="sidebar-overlay" onClick={() => setOpen(false)} />}

      <aside className={`sidebar${open ? ' sidebar-open' : ''}`}>
        <div className="sidebar-logo">
          <h2 title={mosque?.name}>{mosque?.name || <>Ilmi<span style={{ color: 'var(--accent)' }}>fy</span></>}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <PlanBadge plan={plan?.plan} isTrial={plan?.is_trial} />
            {isViewer && (
              <span style={{
                fontSize: 11, fontWeight: 600, background: '#E8F0FE', color: '#1A56DB',
                borderRadius: 4, padding: '2px 7px', letterSpacing: 0.3,
              }}>Betrachter</span>
            )}
          </div>
        </div>

        <nav className="sidebar-nav">
          <NavItem to="/admin" label="Dashboard" icon="dashboard" end />
          <NavItem to="/admin/quizzes" label="Quiz-Verwaltung" icon="quiz" />
          {plan?.plan && plan.plan !== 'free'
            ? <NavItem to="/admin/tournaments" label="Turniere" icon="trophy" />
            : <LockedNavItem to="/admin/tournaments" label="Turniere" icon="trophy" />}
          <NavItem to="/admin/questions" label="Fragen" icon="questions" />
          {hasFeature('stats')
            ? <NavItem to="/admin/stats" label="Statistiken" icon="stats" />
            : <LockedNavItem to="/admin/stats" label="Statistiken" icon="stats" />}
          <NavItem to="/admin/participants" label="Teilnehmer-Verlauf" icon="users" />
          {/* Fshehur përkohësisht — do rregullohet më vonë */}
          {/* <NavItem to="/admin/invoices" label="Rechnungen" icon="invoices" /> */}
          <NavItem to="/admin/profile" label="Profil & Einstellungen" icon="profile" />
        </nav>

        <div className="sidebar-footer">
          <div style={{ padding: '0 4px 12px' }}>
            {plan?.expires_at && (() => {
              const ms = new Date(plan.expires_at) - new Date();
              const days = Math.ceil(ms / 86400000);
              if (ms <= 0) return (
                <div style={{
                  fontSize: 12, color: 'var(--error)', background: 'var(--error-lt)',
                  padding: '8px 10px', borderRadius: 6, marginBottom: 8, fontWeight: 600, lineHeight: 1.4,
                }}>
                  ⚠ Plan abgelaufen — Premium-Funktionen deaktiviert. Bitte verlängern.
                </div>
              );
              if (days <= 14) return (
                <div style={{
                  fontSize: 12, color: days <= 3 ? 'var(--error)' : '#B8650C',
                  background: days <= 3 ? 'var(--error-lt)' : '#FEF3E8',
                  padding: '6px 10px', borderRadius: 6, marginBottom: 8,
                }}>
                  Plan läuft ab in {days} Tag{days !== 1 ? 'en' : ''}
                </div>
              );
              return null;
            })()}
          </div>
          <button
            className="btn btn-ghost w-full"
            style={{ justifyContent: 'center', gap: 8, fontSize: 13 }}
            onClick={() => { logout(); navigate('/login'); }}
          >
            <Icon d={ICONS.logout} size={15} /> Abmelden
          </button>
        </div>
      </aside>
    </>
  );
};
